import React, { Fragment, useEffect } from 'react';
import { Row, Col, Card, Statistic, Typography, Button, Table, Flex, Tag, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';

import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { getDashboardStats, getCalendarEvents } from './redux/slice';
import { primary, secondary, warning, muted } from '../../utils/theme/config';

dayjs.extend(isSameOrBefore);

const { Title, Text } = Typography;

const EVENT_COLORS = {
    contractEnd: '#cf1322',
    noticePeriod: '#fa8c16',
    renewal: '#1677ff',
    payment: '#52c41a',
};

const EVENT_LABELS = {
    contractEnd: 'Vertragsende',
    noticePeriod: 'Kündigungsfrist',
    renewal: 'Verlängerung',
    payment: 'Zahlung',
};

const statusColors = {
    active: primary,
    cancelled: secondary,
    expired: '#fa8c16',
    archived: muted,
};

const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '—';
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
};

const ContractDashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const auth = useSelector(state => state.auth);
    const contracts = useSelector(state => state.contracts);
    const { dashboard, loading, calendarEvents } = contracts;
    const missingContracts = dashboard?.missingDataContracts ?? [];

    const upcomingEvents = (() => {
        const today = dayjs();
        const limit = today.add(60, 'day');
        const seen = new Set();
        return (calendarEvents || [])
            .filter((evt) => {
                const d = dayjs(evt.start);
                return d.isSameOrBefore(limit) && !d.isBefore(today);
            })
            .sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf())
            .filter((evt) => {
                const key = `${evt.contractId}-${evt.reminderType}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
    })();

    useEffect(() => {
        if (auth?.decodeToken?.organizer) {
            dispatch(getDashboardStats(auth.decodeToken.organizer));
            dispatch(getCalendarEvents(auth.decodeToken.organizer));
        }
    }, [auth?.decodeToken?.organizer]);

    const topContractsColumns = [
        {
            title: 'Vertragsname',
            dataIndex: 'name',
            key: 'name',
            ellipsis: true,
        },
        {
            title: 'Partner',
            dataIndex: 'partner',
            key: 'partner',
            ellipsis: true,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={statusColors[status] || muted}>
                    {status === 'active' ? 'Aktiv' : status === 'cancelled' ? 'Gekündigt' : status === 'expired' ? 'Abgelaufen' : 'Archiviert'}
                </Tag>
            ),
        },
        {
            title: 'Kosten',
            dataIndex: 'costAmount',
            key: 'costAmount',
            render: (val, record) => `${formatCurrency(val)} / ${record.costInterval === 'monthly' ? 'mtl.' : record.costInterval === 'yearly' ? 'jährl.' : record.costInterval === 'quarterly' ? 'quart.' : 'einm.'}`,
        },
    ];

    return (
        <Fragment>
            <Row justify='space-between' align='middle' className='bg-white padding-1 rounded-1' gutter={[16, 16]}>
                <Col>
                    <Title level={4} type='success'>Vertragsmanager – Dashboard</Title>
                </Col>
                <Col>
                    <Flex gap={8}>
                        <Button onClick={() => navigate('/contract-manager/chat')}>
                            KI-Chat
                        </Button>
                        <Button type='primary' onClick={() => navigate('/contract-manager/add')}>
                            Neuer Vertrag
                        </Button>
                    </Flex>
                </Col>
            </Row>

            <div className='padding-top-1'>
                {loading && !dashboard ? (
                    <Flex justify='center' align='center' style={{ minHeight: 300 }}>
                        <Spin size='large' />
                    </Flex>
                ) : (
                    <Fragment>
                        <Row gutter={[16, 16]}>
                            <Col xxl={4} xl={6} lg={8} md={12} sm={12} xs={24}>
                                <Card className='border-green'>
                                    <Statistic
                                        title='Aktive Verträge'
                                        value={dashboard?.activeContracts ?? 0}
                                        valueStyle={{ color: primary, fontSize: 32, fontWeight: 700 }}
                                    />
                                </Card>
                            </Col>
                            <Col xxl={4} xl={6} lg={8} md={12} sm={12} xs={24}>
                                <Card className='border-green'>
                                    <Statistic
                                        title='Monatliche Kosten'
                                        value={formatCurrency(dashboard?.monthlyCost ?? 0)}
                                        valueStyle={{ color: primary, fontSize: 22, fontWeight: 700 }}
                                        formatter={(val) => val}
                                    />
                                </Card>
                            </Col>
                            <Col xxl={4} xl={6} lg={8} md={12} sm={12} xs={24}>
                                <Card className='border-green'>
                                    <Statistic
                                        title='Jährliche Kosten'
                                        value={formatCurrency(dashboard?.yearlyCost ?? 0)}
                                        valueStyle={{ color: primary, fontSize: 22, fontWeight: 700 }}
                                        formatter={(val) => val}
                                    />
                                </Card>
                            </Col>
                            <Col xxl={4} xl={6} lg={8} md={12} sm={12} xs={24}>
                                <Card className='border-green'>
                                    <Statistic
                                        title='Ablauf in 30 Tagen'
                                        value={dashboard?.expiringIn30Days ?? 0}
                                        valueStyle={{ color: warning, fontSize: 32, fontWeight: 700 }}
                                    />
                                </Card>
                            </Col>
                            <Col xxl={4} xl={6} lg={8} md={12} sm={12} xs={24}>
                                <Card className='border-green'>
                                    <Statistic
                                        title='Kündigungsfristen (30 Tage)'
                                        value={dashboard?.noticeDeadlinesIn30Days ?? 0}
                                        valueStyle={{ color: secondary, fontSize: 32, fontWeight: 700 }}
                                    />
                                </Card>
                            </Col>
                            <Col xxl={4} xl={6} lg={8} md={12} sm={12} xs={24}>
                                <Card className='border-green'>
                                    <Statistic
                                        title='Auto-Verlängerung'
                                        value={dashboard?.autoRenewalCount ?? 0}
                                        valueStyle={{ color: '#1677ff', fontSize: 32, fontWeight: 700 }}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]} className='padding-top-1'>
                            <Col xxl={14} xl={14} lg={24} md={24} sm={24} xs={24}>
                                <Card title={<Text strong>Top 5 teuerste Verträge</Text>} className='border-green'>
                                    <Table
                                        columns={topContractsColumns}
                                        dataSource={dashboard?.topContracts ?? []}
                                        rowKey={(r) => r._id || r.name}
                                        pagination={false}
                                        size='small'
                                        onRow={(record) => ({
                                            onClick: () => record._id && navigate(`/contract-manager/detail/${record._id}`),
                                            style: { cursor: record._id ? 'pointer' : 'default' },
                                        })}
                                    />
                                </Card>
                            </Col>

                            <Col xxl={10} xl={10} lg={24} md={24} sm={24} xs={24}>
                                <Card title={<Text strong>Kosten nach Kategorie</Text>} className='border-green'>
                                    {(dashboard?.costByCategory ?? []).length === 0 ? (
                                        <Text type='secondary'>Keine Daten vorhanden</Text>
                                    ) : (
                                        (dashboard.costByCategory).map((item, idx) => (
                                            <Flex key={idx} justify='space-between' align='center' style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                                                <Text>{item.category || 'Sonstige'}</Text>
                                                <Text strong style={{ color: primary }}>{formatCurrency(item.totalCost)}</Text>
                                            </Flex>
                                        ))
                                    )}
                                </Card>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]} className='padding-top-1'>
                            <Col xs={24}>
                                <Card
                                    title={<Text strong>Bevorstehende Fristen (nächste 60 Tage)</Text>}
                                    className='border-green'
                                    extra={
                                        <Button size='small' onClick={() => navigate('/contract-manager/calendar')}>
                                            Alle anzeigen
                                        </Button>
                                    }
                                >
                                    {upcomingEvents.length === 0 ? (
                                        <Text type='secondary'>Keine bevorstehenden Fristen in den nächsten 60 Tagen</Text>
                                    ) : (
                                        <Table
                                            dataSource={upcomingEvents}
                                            rowKey={(r) => `${r.contractId}-${r.reminderType}`}
                                            pagination={false}
                                            size='small'
                                            onRow={(record) => ({
                                                onClick: () => record.contractId && navigate(`/contract-manager/detail/${record.contractId}`),
                                                style: { cursor: record.contractId ? 'pointer' : 'default' },
                                            })}
                                            columns={[
                                                {
                                                    title: 'Datum',
                                                    key: 'start',
                                                    width: 110,
                                                    render: (_, r) => (
                                                        <Text style={{ color: dayjs(r.start).diff(dayjs(), 'day') <= 7 ? '#cf1322' : 'inherit' }}>
                                                            {dayjs(r.start).format('DD.MM.YYYY')}
                                                        </Text>
                                                    ),
                                                },
                                                {
                                                    title: 'Vertragsname',
                                                    dataIndex: 'contractName',
                                                    key: 'contractName',
                                                    ellipsis: true,
                                                },
                                                {
                                                    title: 'Typ',
                                                    key: 'reminderType',
                                                    width: 150,
                                                    render: (_, r) => (
                                                        <Tag color={EVENT_COLORS[r.reminderType] || muted}>
                                                            {EVENT_LABELS[r.reminderType] || r.reminderType}
                                                        </Tag>
                                                    ),
                                                },
                                                {
                                                    title: 'Tage',
                                                    key: 'days',
                                                    width: 80,
                                                    render: (_, r) => {
                                                        const days = dayjs(r.start).diff(dayjs(), 'day');
                                                        return (
                                                            <Text strong style={{ color: days <= 7 ? '#cf1322' : days <= 14 ? '#fa8c16' : 'inherit' }}>
                                                                {days === 0 ? 'Heute' : `in ${days} Tagen`}
                                                            </Text>
                                                        );
                                                    },
                                                },
                                            ]}
                                        />
                                    )}
                                </Card>
                            </Col>
                        </Row>

                        {missingContracts.length > 0 && (
                            <Row gutter={[16, 16]} className='padding-top-1'>
                                <Col xs={24}>
                                    <Card
                                        title={<Text strong style={{ color: secondary }}>Fehlende Daten</Text>}
                                        style={{ borderColor: secondary }}
                                    >
                                        <Text type='danger'>
                                            {missingContracts.length} Vertrag/Verträge ohne Enddatum:
                                        </Text>
                                        <Flex wrap gap={8} style={{ marginTop: 8 }}>
                                            {missingContracts.map((c) => (
                                                <Link key={c._id} to={`/contract-manager/detail/${c._id}`}>
                                                    <Tag color='error' style={{ cursor: 'pointer' }}>
                                                        {c.name}
                                                    </Tag>
                                                </Link>
                                            ))}
                                        </Flex>
                                    </Card>
                                </Col>
                            </Row>
                        )}
                    </Fragment>
                )}
            </div>
        </Fragment>
    );
};

export default ContractDashboard;
